import { Field, FieldProps, Formik, FormikProps } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import useInitialErrors from '../../hooks/formik-initial-errors';
import { object as objectYup, string as stringYup, ref as refYup } from 'yup';
import { passwordRegExp } from '../../lib/utils/validation';
import { TextInput } from '../forms/TextInput';
import { createValidationSchema } from '../../types/formValidation';

interface Props {
    submitPassowrdData: (base64PassString: string) => void;
    setStep?: (key: string) => void;
    userEmail?: string;
}

const initialFormData = {
    password: '',
    cpassword: '',
};

const CreatePassword: React.FC<Props> = ({ submitPassowrdData, setStep, userEmail }) => {
    const [base64PassString, setBase64PassString] = useState('');
    const [loader, setLoader] = useState(false);

    const validationSchema = useMemo(() => createValidationSchema(false, false, false, false, true), []);
    const initialErrors = useInitialErrors(initialFormData, validationSchema);

    const submitForm = useCallback(async () => {
        try {
            setLoader(true);
            submitPassowrdData(base64PassString);
            setTimeout(() => {
                setStep?.('complete-profile');
                setLoader(false);
            }, 1000);
        } catch (error) {
            setLoader(false);
        }
    }, [base64PassString, setStep]);

    const convertToBase64 = (value: string) => {
        const encodedString = btoa(value);
        setBase64PassString(encodedString);
    };

    return (
        <div className="tab-pane " id="createpassword-tab" role="tabpanel" aria-labelledby="createpassword">
            <div className="fs-20 fw-600 mb-3 text-center">Create password</div>
            <div className="row align-items-center">
                <div className="col-md-8 mx-auto">
                    <div className="card lt-shadow-sm border-radius-12">
                        <div className="card-body">
                            <Formik
                                onSubmit={submitForm}
                                enableReinitialize
                                initialValues={initialFormData}
                                validationSchema={getValidationSchema()}
                                initialErrors={initialErrors}
                            >
                                {(formikProps: FormikProps<any>) => {
                                    const { handleSubmit, values, isSubmitting, setFieldTouched, setFieldValue } =
                                        formikProps;

                                    return (
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="mb-0">
                                                        <label className="fs-14 fw-400 mb-2">
                                                            Email
                                                            <span className="lt-text-error">*</span>
                                                        </label>
                                                        <input
                                                            className="form-control mb-3"
                                                            required
                                                            value={userEmail}
                                                            name="text"
                                                            type="email"
                                                            disabled
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="mb-0">
                                                        <TextInput
                                                            label="Password"
                                                            required
                                                            name="password"
                                                            type="password"
                                                            placeholder="Enter Password"
                                                        />
                                                        <div className="d-flex justify-content-start mt-2">
                                                            <ul className="fs-12 lt-text-disabled row">
                                                                <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                    One uppercase character
                                                                </li>
                                                                <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                    One lowercase character
                                                                </li>
                                                                <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                    One special character
                                                                </li>
                                                                <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                    One number
                                                                </li>
                                                                <li className="lt-text-disabled col-12 px-0 pb-1">
                                                                    Should be 8 - 16 characters
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="mb-4">
                                                        <TextInput
                                                            label="Confirm Password"
                                                            required
                                                            name="cpassword"
                                                            type="password"
                                                            placeholder="Confirm Password"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="text-end">
                                                        <button
                                                            type="submit"
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                convertToBase64(values.cpassword);
                                                            }}
                                                            disabled={isSubmitting || loader}
                                                            className="btn btn-primary w-100 h-44 mb-4"
                                                            formNoValidate
                                                        >
                                                            {loader && (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            )}{' '}
                                                            Create Password
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    );
                                }}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreatePassword;

const getValidationSchema = () => {
    return objectYup().shape({
        password: stringYup()
            .required('Password is required.')
            .min(8, 'Password must be in between 8 - 16 characters.')
            .matches(passwordRegExp, 'Password must match the below conditions.'),
        cpassword: stringYup()
            .nullable()
            .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
            .required('Confirm Password is required.'),
    });
};
